import {
  IAppointmentCartItemsMappingListResponseData,
  IAppointmentCartItemsMappingUpdateFilterBody,
} from "@/api_framework/api_modals/Mapping";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import clsx from "clsx";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useConfigurationModuleSearchAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationModuleSearchAPI";
import { useAppointmentCartItemsMappingDetailAPI } from "@/app/hooks/api_hooks/Mappings/useAppointmentCartItemsMappingDetailAPI";
import { useAppointmentCartItemsMappingListAPI } from "@/app/hooks/api_hooks/Mappings/useAppointmentCartItemsMappingListAPI";
import { useAppointmentCartItemsMappingOnboardAPI } from "@/app/hooks/api_hooks/Mappings/useAppointmentCartItemsMappingOnboardAPI";
import { useAppointmentCartItemsMappingUpdateAPI } from "@/app/hooks/api_hooks/Mappings/useAppointmentCartItemsMappingUpdateAPI";
import { useOrgServicesByFilterAPI } from "@/app/hooks/api_hooks/org/useOrgServicesByFilterAPI";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { IConfigurationListState } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { IMappingsState } from "@/app_redux/reducers/slices/auth/mapping_slice";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { FaProductHunt, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";

type ItemSearchType = "bom" | "service";

interface ISelectedItem {
  id: string;
  name: string;
  type: ItemSearchType;
}

export interface IAppointmentCartItemsMappingProps {
  service: string;
  serviceName: string;
  subService: string;
  subServiceName: string;
}

const AppointmentCartItemMappingOnboardPage = () => {
  const navigation = useNavigate();
  const [searchParams] = useSearchParams();
  const appointmentCartItemMappingId = searchParams.get("appointment_cart_item_mapping_id");
  const isEditMode = Boolean(appointmentCartItemMappingId);
  const lastFetchedMappingIdRef = useRef<string | null>(null);
  const hydratedMappingIdRef = useRef<string | null>(null);

  const { serviceTypes, execute: fetchOrgServicesByFilter } = useOrgServicesByFilterAPI();
  const serviceTypeItems = useSelectMenuReducer(serviceTypes, "name", "id");
  const { getRouteKey } = useRouter();
  const { routeState } = useRouteInfo(getRouteKey("CONFIGURATION", "id"));
  const { bomList, serviceList } = routeState.state as IConfigurationListState;
  const { routeState: mappingsRouteState } = useRouteInfo(getRouteKey("DATA_MAPPINGS", "id"));
  const { appointmentCartItemMappingDetail } = mappingsRouteState.state as IMappingsState;
  const mappingDetail: any = Array.isArray(appointmentCartItemMappingDetail)
    ? appointmentCartItemMappingDetail[0]
    : appointmentCartItemMappingDetail;
  const searchBomItems = useConfigurationModuleSearchAPI("bom");
  const searchServiceItems = useConfigurationModuleSearchAPI("service");
  const { execute: onboardAppointmentCartItemsMapping } = useAppointmentCartItemsMappingOnboardAPI();
  const { execute: updateAppointmentCartItemsMapping } = useAppointmentCartItemsMappingUpdateAPI();
  const { execute: fetchAppointmentCartItemsMappingDetail } = useAppointmentCartItemsMappingDetailAPI();
  const fetchAppointmentCartItemsMappingList = useAppointmentCartItemsMappingListAPI();
  const itemSearchContainerRef = useRef<HTMLDivElement>(null);
  const [isItemSearchOpen, setIsItemSearchOpen] = useState(false);
  const [itemSearchType, setItemSearchType] = useState<ItemSearchType>("bom");
  const [itemSearchQuery, setItemSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState<ISelectedItem[]>([]);
  const itemSearchResults = itemSearchQuery ? (itemSearchType === "bom" ? bomList : serviceList) : [];
  const [isSubmitting, setIsSubmitting] = useState(false);
  const itemSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const MIN_SEARCH_LENGTH = 3;

  const formHook = useForm<IAppointmentCartItemsMappingProps>({
    mode: "onChange",
    defaultValues: {},
  });

  const selectedServiceId = formHook.watch("service");
  const selectedSubServiceId = formHook.watch("subService");
  const isItemSearchDisabled = isEditMode ? !selectedSubServiceId : !selectedServiceId || !selectedSubServiceId;

  const searchItems = (query: string, type: ItemSearchType = itemSearchType) => {
    if (isItemSearchDisabled) return;
    setItemSearchQuery(query);
    if (!query) return;
    if (type === "bom") {
      searchBomItems(query);
    } else {
      searchServiceItems(query);
    }
  };

  const onItemSearchInput = (event: React.FormEvent<HTMLInputElement>) => {
    if (isItemSearchDisabled) return;
    const value = event.currentTarget.value;
    if (itemSearchDebounceRef.current) clearTimeout(itemSearchDebounceRef.current);
    if (value.length < MIN_SEARCH_LENGTH) {
      setItemSearchQuery("");
      return;
    }
    itemSearchDebounceRef.current = setTimeout(() => {
      searchItems(value);
    }, 300);
  };

  const onSelectItemSearchType = (type: ItemSearchType) => {
    if (isItemSearchDisabled) return;
    setItemSearchType(type);
    searchItems(itemSearchQuery, type);
  };

  const onSelectSearchedItem = (item: any) => {
    const id = itemSearchType === "bom" ? item?.bom_id : item?.sub_service_id;
    const name = itemSearchType === "bom" ? item?.bom_name : item?.sub_service_name;
    if (!id) return;
    setSelectedItems((prev) =>
      prev.some((selectedItem) => selectedItem.id === id && selectedItem.type === itemSearchType)
        ? prev
        : [...prev, { id, name, type: itemSearchType }],
    );
    setIsItemSearchOpen(false);
  };

  const onRemoveSelectedItem = (id: string, type: ItemSearchType) => {
    setSelectedItems((prev) => prev.filter((selectedItem) => !(selectedItem.id === id && selectedItem.type === type)));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (itemSearchContainerRef.current && !itemSearchContainerRef.current.contains(event.target as Node)) {
        setIsItemSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (itemSearchDebounceRef.current) clearTimeout(itemSearchDebounceRef.current);
    };
  }, []);

  const subServices = useMemo(() => {
    const selectedServiceType = serviceTypes?.find((serviceType) => serviceType.id === selectedServiceId);
    return selectedServiceType?.services?.flatMap((service) => service.sub_services) ?? [];
  }, [serviceTypes, selectedServiceId]);
  const l2ServiceItems: ISelectMenuItemData[] = useSelectMenuReducer(subServices, "sub_service_name", "sub_service_id");

  useEffect(() => {
    fetchOrgServicesByFilter({ filterType: "booking_vms" });
  }, []);

  useEffect(() => {
    if (isEditMode) return;
    formHook.setValue("subService", "");
    formHook.setValue("subServiceName", "");
  }, [selectedServiceId]);

  useEffect(() => {
    if (isItemSearchDisabled) {
      setIsItemSearchOpen(false);
      setItemSearchQuery("");
    }
  }, [isItemSearchDisabled]);

  useEffect(() => {
    if (!isEditMode || lastFetchedMappingIdRef.current === appointmentCartItemMappingId) return;
    lastFetchedMappingIdRef.current = appointmentCartItemMappingId;
    fetchAppointmentCartItemsMappingDetail(appointmentCartItemMappingId);
  }, [isEditMode, appointmentCartItemMappingId]);

  useEffect(() => {
    if (!isEditMode || !mappingDetail) return;
    if (mappingDetail.sub_service_id !== appointmentCartItemMappingId) return;
    if (hydratedMappingIdRef.current === appointmentCartItemMappingId) return;
    formHook.setValue("subService", mappingDetail.sub_service_id);
    formHook.setValue("subServiceName", mappingDetail.sub_service_name);
    setSelectedItems(
      (mappingDetail.sub_services_map ?? []).map((item: any) => ({
        id: item.sub_service_id,
        name: item.sub_service_name,
        type: "service" as ItemSearchType,
      })),
    );
    hydratedMappingIdRef.current = appointmentCartItemMappingId;
  }, [isEditMode, mappingDetail]);

  const onSubmit = async () => {
    if (!selectedItems.length) return;
    setIsSubmitting(true);
    const subServicesMap = selectedItems.map((item) => ({
      sub_service_id: item.id,
      sub_service_name: item.name,
    }));
    const response = isEditMode
      ? await updateAppointmentCartItemsMapping(selectedSubServiceId, {
          sub_services_map: subServicesMap,
        } as IAppointmentCartItemsMappingUpdateFilterBody)
      : await onboardAppointmentCartItemsMapping({
          sub_service_id: selectedSubServiceId,
          sub_service_name: formHook.watch("subServiceName"),
          sub_services_map: subServicesMap,
        } as IAppointmentCartItemsMappingListResponseData);
    setIsSubmitting(false);
    if (response?.status) {
      await fetchAppointmentCartItemsMappingList("");
      navigation(`/app/mappings/appointment-cart-item-mapping`);
    }
  };

  const handelBackStepper = () => {
    navigation(`/app/mappings/appointment-cart-item-mapping`);
  };

  return (
    <div className="bg-white py-1 rounded-md">
      <div className="mx-3 mt-2 font-semibold text-base text-black">Appointment Cart Items Mapping Onboard</div>
      <div className="mx-3">
        <form onSubmit={formHook?.handleSubmit(onSubmit)}>
          {!isEditMode && (
            <div className="flex justify-start items-baseline mt-8">
              <div className="flex justify-start items-center gap-1 p-2.5 w-[200px] h-[38px]">
                <span className="font-normal text-black text-xs">Service Name</span>
                <span className="font-normal text-rose-600 text-xs">*</span>
              </div>
              <SearchableSelectMenu
                onSelectItem={(item) => {
                  formHook.setValue("service", item.id);
                  formHook.setValue("serviceName", item.title);
                  formHook.clearErrors("service");
                }}
                errorMessages={[{ message: "Service Type Required", type: "required" }]}
                fieldError={formHook.formState.errors.service}
                register={formHook.register("service", {
                  required: true,
                })}
                showDropdownIcon
                showTypedErrors={false}
                placeholder="Service Type"
                selectItems={serviceTypeItems}
                defaultSelected={serviceTypeItems?.filter((l1s) => l1s.id === formHook.watch("service"))[0]}
                containerClassName="w-[380px]"
              />
            </div>
          )}
          <div className="flex justify-start items-baseline mt-8">
            <div className="flex justify-start items-center gap-1 p-2.5 w-[200px] h-[38px]">
              <span className="font-normal text-black text-xs">Sub Service Name</span>
              <span className="font-normal text-rose-600 text-xs">*</span>
            </div>
            {isEditMode ? (
              <div className="w-[380px] h-[38px] flex items-center px-3 rounded-md bg-[#f4f4f4] text-xs text-gray-700">
                {formHook.watch("subServiceName")}
              </div>
            ) : (
              <SearchableSelectMenu
                onSelectItem={(item) => {
                  formHook.setValue("subService", item.id);
                  formHook.setValue("subServiceName", item.title);
                  formHook.clearErrors("subService");
                }}
                showTooltips
                errorMessages={[{ message: "Sub Service Required", type: "required" }]}
                fieldError={formHook.formState.errors.subService}
                register={formHook.register("subService", {
                  required: true,
                })}
                placeholder="Sub Service"
                showDropdownIcon
                showTypedErrors={false}
                containerClassName="w-[380px]"
                selectItems={l2ServiceItems}
                defaultSelected={l2ServiceItems?.filter((l1s) => l1s.id === formHook.watch("subService"))[0]}
              />
            )}
          </div>
          <div className="flex justify-start items-baseline mt-8">
            <div className="flex justify-start items-center gap-1 p-2.5 w-[200px] h-[38px]">
              <span className="font-normal text-black text-xs">Items</span>
              <span className="font-normal text-rose-600 text-xs">*</span>
            </div>
            <div
              className={`relative w-[500px] ${isItemSearchDisabled ? "opacity-50 grayscale cursor-not-allowed" : ""}`}
              ref={itemSearchContainerRef}
            >
              <InputWithAddOn
                onFocus={() => {
                  if (!isItemSearchDisabled) setIsItemSearchOpen(true);
                }}
                onEnterKeyPress={(query: string) => {
                  searchItems(query);
                }}
                onInput={onItemSearchInput}
                disabled={isItemSearchDisabled}
                containerClassName={`w-full ${isItemSearchDisabled ? "pointer-events-none" : ""}`}
                addon={<FaProductHunt className="w-4 h-4" />}
                placeholder={isItemSearchDisabled ? "Select service and sub service first" : "Search Items"}
              />
              {isItemSearchOpen && !isItemSearchDisabled && (
                <div className="z-10 absolute mt-1 w-full rounded-md bg-white shadow-lg">
                  <div className="flex gap-10">
                    <div
                      className={`flex-1 text-center py-2 text-xs font-bold cursor-pointer rounded-2xl ${
                        itemSearchType === "bom"
                          ? "bg-black text-white border-black"
                          : "text-[#4d4f53] bg-[#f4f4f4] border-[#e2e2e2]"
                      }`}
                      onClick={() => onSelectItemSearchType("bom")}
                    >
                      BOM
                    </div>
                    <div
                      className={`flex-1 text-center py-2 text-xs font-bold cursor-pointer rounded-2xl ${
                        itemSearchType === "service"
                          ? "bg-black text-white border-black"
                          : "text-[#4d4f53] bg-[#f4f4f4] border-[#e2e2e2]"
                      }`}
                      onClick={() => onSelectItemSearchType("service")}
                    >
                      Service
                    </div>
                  </div>
                  <div className="max-h-60 overflow-auto">
                    {itemSearchResults?.length > 0 ? (
                      itemSearchResults.map((item: any, index: number) => (
                        <div
                          key={index}
                          className="px-4 py-2 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer"
                          onClick={() => onSelectSearchedItem(item)}
                        >
                          {itemSearchType === "bom" ? item?.bom_name : item?.sub_service_name}
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-center text-xs text-gray-500">No results found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          {selectedItems.length > 0 && (
            <div className="mt-10 w-full mb-16">
              <div className="outer-container relative rounded-xl bg-[#F9FAFB]">
                <div className="scroll-inner !h-auto max-h-[400px]">
                  <div className="w-full">
                    <div className="inline-block min-w-full align-middle">
                      <table className="w-full min-w-[480px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                        <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                          <tr>
                            {["Type", "Item Name", "Action"].map((col) => (
                              <th
                                key={col}
                                scope="col"
                                className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize"
                              >
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="overflow-hidden rounded-xl w-full">
                          {selectedItems.map((item, index) => {
                            const isFirst = index === 0;
                            const isLast = index === selectedItems.length - 1;
                            const isSingle = selectedItems.length === 1;

                            return (
                              <tr
                                key={`${item.type}-${item.id}`}
                                className={clsx(
                                  "group bg-white transition",
                                  isSingle && "rounded-xl",
                                  isFirst && !isSingle && "rounded-t-xl",
                                  isLast && !isSingle && "rounded-b-xl",
                                )}
                              >
                                <td
                                  className={clsx(
                                    "px-3 py-3 whitespace-nowrap text-xs font-normal capitalize text-[#6C727F]",
                                    isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                    isFirst && !isSingle && "rounded-tl-2xl",
                                    isLast && !isSingle && "rounded-bl-2xl",
                                  )}
                                >
                                  {item.type}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]">
                                  {item.name}
                                </td>
                                <td
                                  className={clsx(
                                    "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                    isSingle && "rounded-tr-2xl rounded-br-2xl",
                                    isFirst && !isSingle && "rounded-tr-2xl",
                                    isLast && !isSingle && "rounded-br-2xl",
                                  )}
                                >
                                  <button
                                    type="button"
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-xl bg-rose-50 text-rose-600 transition hover:bg-rose-100"
                                    onClick={() => onRemoveSelectedItem(item.id, item.type)}
                                  >
                                    <FaTrash className="w-3.5 h-3.5" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="right-5 bottom-0 fixed flex justify-end items-center gap-4 border-t-2 w-full p-3 bg-white">
            <button
              type="button"
              onClick={() => handelBackStepper()}
              className={`${SECONDARY_BUTTON} mt-3 w-full sm:mt-0 sm:w-auto`}
            >
              Back
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`${PRIMARY_BUTTON} mt-3 w-full sm:mt-0 sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              {isSubmitting && (
                <svg
                  className="animate-spin mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentCartItemMappingOnboardPage;
